import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ProjectService } from '../../services/project/project.service';
import { ProjectCreate } from '../../interfaces/project.interface';
import { AppStateService } from '../../services/app-state/app-state.service';
import { ProjectEventsService } from '../../services/project-events/project-events.service';
import {
  FeatureService,
  Feature,
} from '../../services/feature/feature.service';
import { debounce } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { AutoUnsubscribe } from '../../decorators/auto-unsubscribe.decorator';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { FileUploadModule } from '@iplab/ngx-file-upload';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

const DEBOUNCE_TIMEOUT_MS = 500;

@AutoUnsubscribe()
@Component({
  standalone: true,
  selector: 'app-add-project-dialog',
  templateUrl: './add-project-dialog.component.html',
  styleUrls: ['./add-project-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatStepperModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatSelectModule,
    MatListModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    FlexLayoutModule,
    FileUploadModule,
  ],
})
export class AddProjectDialogComponent implements OnInit, OnDestroy {
  data = inject(MAT_DIALOG_DATA);
  private _formBuilder = inject(FormBuilder);
  private projectService = inject(ProjectService);
  private featureService = inject(FeatureService);
  private dialogRef = inject<MatDialogRef<AddProjectDialogComponent>>(MatDialogRef);
  private appStateService = inject(AppStateService);
  private readonly projectEvents = inject(ProjectEventsService);
  private readonly toastr = inject(ToastrService);

  public features = signal<Feature[]>([]);
  public selectedFeatures: number[] = [];

  projectForm = this._formBuilder.group({
    name: ['', Validators.required],
    description: ['', Validators.required],
    workflowName: ['', Validators.required],
  });

  ngOnInit() {
    this.featureService.getFeatures().subscribe(
      (features) => {
        this.features.set(features);
      },
      (error) => {
        console.error('Failed to load features:', error);
        this.toastr.error('Failed to load features');
      },
    );
  }

  // eslint-disable-next-line
  ngOnDestroy() {
    // This is intentional
  }

  onFeatureSelectionChange(event: MatSelectionListChange): void {
    this.selectedFeatures = event.source.selectedOptions.selected
      .map((option) => {
        const feature = this.features().find((f) => f.featureId === option.value);
        return feature ? feature.featureId : null;
      })
      .filter((id): id is number => id !== null);
  }

  private buildProjectData(formValue: any): ProjectCreate {
    const {
      value: { name, description, workflowName },
    } = formValue;

    return {
      name: String(name).trim(),
      description: String(description).trim(),
      workflowName: String(workflowName).trim(),
    };
  }

  private async addFeaturesToProject(projectId: number): Promise<void> {
    if (this.selectedFeatures.length === 0) {
      return;
    }

    for (const featureId of this.selectedFeatures) {
      try {
        await firstValueFrom(
          this.projectService.addProjectIntegration(projectId, featureId),
        );
      } catch (featureError) {
        console.error(`Failed to add feature ${featureId}:`, featureError);
        this.toastr.warning(`Failed to add feature to the project`);
      }
    }
  }

  private handleProjectCreationSuccess(createdProject: any): void {
    this.projectEvents.notifyCreated(createdProject.projectId);

    if (createdProject.projectId && createdProject.currentVersionId) {
      this.appStateService.navigateToProject(
        createdProject.projectId,
        createdProject.currentVersionId,
      );
    }
  }

  private async createProject(formValue: any): Promise<void> {
    const projectData = this.buildProjectData(formValue);

    const createdProject = await firstValueFrom(
      this.projectService.create(projectData),
    );

    await this.addFeaturesToProject(createdProject.projectId as number);
    await this.appStateService.loadProjects();
    this.handleProjectCreationSuccess(createdProject);

    this.dialogRef.close();
    this.toastr.success('Project created successfully');
  }

  public submitForm = debounce(
    async (formValue: any, isValid: boolean, $event: Event) => {
      $event.preventDefault();

      if (!isValid) {
        return;
      }

      try {
        await this.createProject(formValue);
      } catch (_error) {
        this.toastr.error('Failed to create project');
      }
    },
    DEBOUNCE_TIMEOUT_MS,
  );
}
